import {
  MUTATION_RESPONSE_RECEIVED,
  MUTATION_REQUIRED,
} from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  SONAR_V3_ALPHA_CREATE_SESSION,
  SONAR_V3_CREATE_SESSION,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import { isSonarV3ErrorPayload } from '@web-platform/shared-universal-utils-frontend/src/helpers/isSonarErrorPayload';
import { logError } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';

import { MUTLIBOOK_OPEN_STATE_CHANGE } from '../../../../actions/detailsPanel';

import type { ActionType } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { ItineraryID } from '@web-platform/shared-universal-types-utils/src/utils';

type State = Record<ItineraryID, boolean>;

export const INITIAL_STATE = {};

// Extract the itinerary identifier from mutation variables, preferring `itineraryId`
// and falling back to `itineraryToken`. Logs and returns undefined when neither is usable.
const getItineraryKey = (
  variables: { itineraryId?: unknown; itineraryToken?: unknown },
  context: 'request' | 'response',
): string | undefined => {
  const identifier = variables.itineraryId || variables.itineraryToken;
  if (!identifier || identifier === 'undefined') {
    logError({
      error: new Error(`Missing itineraryId/Token in SONAR_V3_CREATE_SESSION ${context}`),
      component: 'hadSuccessfulBookingMutation',
      message: `Received SONAR_V3_CREATE_SESSION mutation ${context} with no itineraryId or itineraryToken`,
    });
    return undefined;
  }
  return String(identifier);
};

export default function (state: State = INITIAL_STATE, action: ActionType): State {
  switch (action.type) {
    case MUTLIBOOK_OPEN_STATE_CHANGE: {
      // `itineraryId` is `Maybe<ID>` on the Acorn variant of this action, so guard
      // against null/undefined before keying the map to avoid `'null'`/`'undefined'` keys.
      if (!action.payload.isOpen) {
        return state;
      }
      const { itineraryId } = action.payload;
      if (!itineraryId || itineraryId === 'undefined') {
        logError({
          error: new Error('Missing itineraryId in MUTLIBOOK_OPEN_STATE_CHANGE'),
          component: 'hadSuccessfulBookingMutation',
          message: 'Received MUTLIBOOK_OPEN_STATE_CHANGE with no itineraryId',
        });
        return state;
      }
      return { ...state, [itineraryId]: false };
    }

    case MUTATION_REQUIRED:
      if (
        action.payload.type === SONAR_V3_CREATE_SESSION ||
        action.payload.type === SONAR_V3_ALPHA_CREATE_SESSION
      ) {
        const key = getItineraryKey(action.payload.variables, 'request');
        if (key === undefined) {
          return state;
        }
        return { ...state, [key]: false };
      }

      return state;

    case MUTATION_RESPONSE_RECEIVED:
      if (
        action.payload.type === SONAR_V3_CREATE_SESSION ||
        action.payload.type === SONAR_V3_ALPHA_CREATE_SESSION
      ) {
        const key = getItineraryKey(action.payload.variables, 'response');
        if (key === undefined) {
          return state;
        }
        return {
          ...state,
          [key]: !isSonarV3ErrorPayload(action.payload.response),
        };
      }

      return state;

    case SEARCH_PARAMS_CHANGED: {
      return INITIAL_STATE;
    }

    default:
      return state;
  }
}
export const get = (state: State): State => state;
